import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Package, Waves, Zap, BarChart3, Eye, Target, Sparkles, Loader2, Check,
  FlaskConical, Calendar, ChevronRight, Copy, FileText, TrendingUp, DollarSign,
  Users, ArrowRight, Send, Clock
} from 'lucide-react';
import { useBrandLabel } from '../lib/demoBrand';
import { useDemoProfile } from '../hooks/useDemoProfile';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#00F5FF';
const P = '#FF6BBD';
const BG = '#0A0A0A';
const CARD = '#121212';
const BORDER = '#1E1E1E';

const SKUS_FALLBACK = [
  { id: 'sku-001', name: 'Electrolyte Drink Mix – Berry Burst', image: 'https://images.unsplash.com/photo-1543362905-f2423ef4e0f8?w=400&h=300&fit=crop', revenue: '$1.2M', roas: '8.4x', velocity: '8.4', impressions: '42.3M', successRate: 94, status: 'Wave-Ready', aiInsight: 'Highest-performing SKU — primed for a Summer Hydration Wave on TikTok', lastWaveDate: 'Mar 2026',
    calendar: [
      { month: 'Jan', wave: 'New Year Hydration Push', budget: '$45K', roas: '9.1x', creators: 312, status: 'completed' },
      { month: 'Feb', wave: 'Berry Burst Challenge', budget: '$38K', roas: '7.9x', creators: 245, status: 'completed' },
      { month: 'Mar', wave: 'Summer Hydration Wave', budget: '$62K', roas: '8.4x', creators: 412, status: 'active' },
      { month: 'Jun', wave: 'Summer Kickoff Wave', budget: '$55K', roas: null, creators: null, status: 'planned' },
      { month: 'Sep', wave: 'Back to School Hydration', budget: '$40K', roas: null, creators: null, status: 'planned' },
      { month: 'Nov', wave: 'Black Friday Bundle Wave', budget: '$75K', roas: null, creators: null, status: 'planned' },
    ],
  },
  { id: 'sku-002', name: 'Sleep Recovery Powder', image: 'https://images.unsplash.com/photo-1763757933292-d8290692edde?w=400&h=300&fit=crop', revenue: '$890K', roas: '7.2x', velocity: '7.2', impressions: '28.1M', successRate: 88, status: 'Strong Performer', aiInsight: 'Evening routine content over-indexes — schedule a Q3 Sleep Campaign', lastWaveDate: 'Mar 2026',
    calendar: [
      { month: 'Mar', wave: 'Sleep Better Wave', budget: '$42K', roas: '7.2x', creators: 298, status: 'active' },
      { month: 'Jul', wave: 'Summer Sleep Reset', budget: '$35K', roas: null, creators: null, status: 'planned' },
      { month: 'Oct', wave: 'Fall Wellness Reset', budget: '$45K', roas: null, creators: null, status: 'planned' },
    ],
  },
  { id: 'sku-003', name: 'Morning Energy Gummies', image: 'https://images.unsplash.com/photo-1483918793747-5adbf82956c4?w=400&h=300&fit=crop', revenue: '$650K', roas: '6.5x', velocity: '6.5', impressions: '19.4M', successRate: 82, status: 'Wave-Ready', aiInsight: 'TikTok unboxing content drives 3.4x more engagement — prioritize short-form', lastWaveDate: 'Feb 2026',
    calendar: [
      { month: 'Feb', wave: 'Morning Ritual Wave', budget: '$32K', roas: '6.5x', creators: 186, status: 'completed' },
      { month: 'May', wave: 'Summer Energy Wave', budget: '$40K', roas: null, creators: null, status: 'planned' },
    ],
  },
  { id: 'sku-004', name: 'Tropical Electrolyte Hydration', image: 'https://images.unsplash.com/photo-1762631178325-2c3aea88f166?w=400&h=300&fit=crop', revenue: '$420K', roas: '7.8x', velocity: '7.8', impressions: '15.2M', successRate: 86, status: 'Strong Performer', aiInsight: 'Tropical flavor resonates with 25-34 demo — expand creator cohort', lastWaveDate: 'Mar 2026',
    calendar: [{ month: 'Mar', wave: 'Tropical Getaway Wave', budget: '$28K', roas: '7.8x', creators: 165, status: 'active' }],
  },
  { id: 'sku-005', name: 'Post-Workout Recovery Blend', image: 'https://images.unsplash.com/photo-1763757933154-d55844eae856?w=400&h=300&fit=crop', revenue: '$780K', roas: '9.1x', velocity: '9.1', impressions: '22.8M', successRate: 90, status: 'Wave-Ready', aiInsight: 'Fitness creators show 9.1x ROAS — top candidate for Performance Wave', lastWaveDate: 'Jan 2026',
    calendar: [{ month: 'Jan', wave: 'Gym Recovery Wave', budget: '$48K', roas: '9.1x', creators: 324, status: 'completed' }],
  },
  { id: 'sku-006', name: 'Daily Wellness Drops', image: 'https://images.unsplash.com/photo-1625865019554-220ea80ea813?w=400&h=300&fit=crop', revenue: '—', roas: '—', velocity: '—', impressions: '—', successRate: 78, status: 'New Opportunity', aiInsight: 'Launching May 2026 — schedule a pre-launch Branding Wave', lastWaveDate: '—',
    calendar: [{ month: 'May', wave: 'Wellness Drops Launch Wave', budget: '$55K', roas: null, creators: null, status: 'planned' }],
  },
  { id: 'sku-007', name: 'Immunity Defense Powder', image: 'https://images.unsplash.com/photo-1763747958224-7726941b0b15?w=400&h=300&fit=crop', revenue: '—', roas: '—', velocity: '—', impressions: '—', successRate: 62, status: 'Needs Attention', aiInsight: 'Low wave success rate — consider R&D test with 25 creators before scaling', lastWaveDate: '—',
    calendar: [],
  },
];

const UNRELEASED_FALLBACK = [
  { id: 'ur-1', name: 'Hydration Stick Packs – Citrus', image: 'https://images.unsplash.com/photo-1574537899275-c8a8188887bb?w=400&h=300&fit=crop' },
  { id: 'ur-2', name: 'Collagen Beauty Elixir', image: 'https://images.unsplash.com/photo-1662673146101-b9ce6400578b?w=400&h=300&fit=crop' },
  { id: 'ur-3', name: 'Focus & Flow Nootropic', image: 'https://images.unsplash.com/photo-1765572757676-9794c597c50d?w=400&h=300&fit=crop' },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const TENTPOLES_FALLBACK = { Jan: 'New Year', Feb: null, Mar: 'Spring Refresh', Apr: null, May: 'Summer Kickoff', Jun: 'Summer Hydration', Jul: null, Aug: 'Back to School', Sep: 'Fall Wellness', Oct: null, Nov: 'Black Friday', Dec: 'Holiday / Winter Immunity' };
const STATUS_COLORS = { 'Wave-Ready': { bg: `${T}15`, text: T, border: `${T}30` }, 'Strong Performer': { bg: '#4ade8015', text: '#4ade80', border: '#4ade8030' }, 'Needs Attention': { bg: '#FFB02015', text: '#FFB020', border: '#FFB02030' }, 'New Opportunity': { bg: `${P}15`, text: P, border: `${P}30` } };
const SORT_OPTIONS = ['Performance Score', 'Revenue Potential', 'Wave Velocity', 'Last Wave Date'];
const QUICK_FILTERS = ['All', 'Wave-Ready', 'Underperforming', 'New Launch'];

const MOCK_TESTS_FALLBACK = [
  { id: 't1', product: 'Hydration Stick Packs – Citrus', image: UNRELEASED_FALLBACK[0].image, creators: 25, duration: '2 Weeks', status: 'Completed', engagement: '6.8%', sentiment: 89, purchaseIntent: 84, positiveFeedback: 'Great on-the-go format, citrus flavor is refreshing', improvements: 'Slightly too sweet for some testers' },
  { id: 't2', product: 'Collagen Beauty Elixir', image: UNRELEASED_FALLBACK[1].image, creators: 10, duration: '1 Week', status: 'In Progress', engagement: '5.2%', sentiment: 82, purchaseIntent: 77, positiveFeedback: 'Beautiful packaging, smooth taste', improvements: 'Needs stronger collagen taste messaging' },
];

function HealthRing({ score }) {
  const c = 2 * Math.PI * 38;
  // Pick colour by score band — explicit branching is clearer than nested ternary.
  let color = P;           // low
  if (score >= 80) color = T;
  else if (score >= 60) color = '#FFB020';
  return (
    <div className="relative w-24 h-24 flex-shrink-0" data-testid="portfolio-health-ring">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 88 88">
        <circle cx="44" cy="44" r="38" stroke={BORDER} strokeWidth="5" fill="none" />
        <circle cx="44" cy="44" r="38" stroke={color} strokeWidth="5" fill="none"
          strokeDasharray={c} strokeDashoffset={c - (score / 100) * c} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s', filter: `drop-shadow(0 0 6px ${color}40)` }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black" style={{ color }}>{score}</span>
        <span className="text-[7px] text-gray-600">/100</span>
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const brandLabel = useBrandLabel();
  const { profile, isDemo } = useDemoProfile();
  // Override hardcoded fixtures with brand-specific data when caller is a
  // demo brand. Non-demo brands continue to see the legacy fallback set.
  const SKUS         = (isDemo && profile?.skus?.length)            ? profile.skus            : SKUS_FALLBACK;
  const UNRELEASED   = (isDemo && profile?.unreleased_skus?.length) ? profile.unreleased_skus : UNRELEASED_FALLBACK;
  const MOCK_TESTS   = (isDemo && profile?.mock_tests?.length)
    ? profile.mock_tests.map((t, i) => ({
        id: `t${i + 1}`,
        product: t.product,
        image: (UNRELEASED.find(u => u.name === t.product) || UNRELEASED[0] || {}).image,
        creators: t.creators,
        duration: t.duration,
        status: t.status,
        engagement: t.engagement,
        sentiment: t.sentiment,
        purchaseIntent: t.purchaseIntent,
        positiveFeedback: t.positiveFeedback,
        improvements: t.improvements,
      }))
    : MOCK_TESTS_FALLBACK;
  // Tentpoles overlay for the wave-calendar modal
  const TENTPOLES = (isDemo && profile?.tentpoles && Object.keys(profile.tentpoles).length)
    ? profile.tentpoles
    : TENTPOLES_FALLBACK;
  const RND_LABEL = (isDemo && profile?.rnd_label)
    ? profile.rnd_label
    : 'Send unreleased products to a small group of trusted creators and get real feedback before launching a full Wave.';

  const [activeTab, setActiveTab] = useState('portfolio');
  const [sortBy, setSortBy] = useState('Performance Score');
  const [quickFilter, setQuickFilter] = useState('All');
  const [calendarSku, setCalendarSku] = useState(null);

  // R&D Lab
  const [testProduct, setTestProduct] = useState('');
  const [testSize, setTestSize] = useState(25);
  const [testDuration, setTestDuration] = useState('2 Weeks');
  const [testNotes, setTestNotes] = useState('');
  const [testLaunched, setTestLaunched] = useState(false);

  const filtered = useMemo(() => {
    let items = [...SKUS];
    if (quickFilter === 'Wave-Ready') items = items.filter(s => s.status === 'Wave-Ready');
    else if (quickFilter === 'Underperforming') items = items.filter(s => s.status === 'Needs Attention');
    else if (quickFilter === 'New Launch') items = items.filter(s => s.status === 'New Opportunity');
    if (sortBy === 'Performance Score') items.sort((a, b) => b.successRate - a.successRate);
    else if (sortBy === 'Revenue Potential') items.sort((a, b) => (parseFloat(b.revenue.replace(/[$,KM]/g, '')) || 0) - (parseFloat(a.revenue.replace(/[$,KM]/g, '')) || 0));
    else if (sortBy === 'Wave Velocity') items.sort((a, b) => (parseFloat(b.velocity) || 0) - (parseFloat(a.velocity) || 0));
    return items;
  }, [sortBy, quickFilter, SKUS]);

  // Recomputed when TENTPOLES changes (i.e. demo profile loads).
  const filledTentpoles = useMemo(() => Object.entries(TENTPOLES).filter(([, v]) => v), [TENTPOLES]);

  const handleLaunchTest = () => { setTestLaunched(true); setTimeout(() => setTestLaunched(false), 3000); };

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 max-w-[1500px] mx-auto relative" style={{ background: BG }} data-testid="portfolio-page">
        <span className="absolute top-3 right-5 text-[7px] text-gray-700 uppercase tracking-widest">Patent Pending</span>

        {/* ═══ HERO BANNER ═══ */}
        <div className="rounded-2xl p-6 mb-6 flex items-center gap-6" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
          <div className="flex flex-col items-center">
            <HealthRing score={84} />
            <p className="text-[8px] uppercase tracking-[0.2em] text-gray-500 font-bold mt-2">Portfolio Health</p>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-black text-white mb-1">Portfolio Command Center</h1>
            <p className="text-xs text-gray-500">{brandLabel} &bull; +19% Wave performance this quarter &bull; {SKUS.filter(s => s.status === 'Wave-Ready').length} SKUs Wave-ready</p>
            <p className="text-[10px] text-gray-600 mt-1.5 leading-relaxed max-w-md">
              Health = blend of SKU performance, wave readiness and roadmap coverage across all products.
            </p>
          </div>
          <Link to="/build-wave">
            <button className="px-6 py-3 rounded-xl text-sm font-bold text-white flex items-center gap-2 hover:brightness-110 transition-all"
                    style={{ background: '#FF4D94', boxShadow: '0 0 22px rgba(255,77,148,0.35)' }}
                    data-testid="hero-launch-wave">
              <Waves className="h-4 w-4" />Plan a new wave
            </button>
          </Link>
        </div>

        {/* ═══ TABS ═══ */}
        <div className="flex gap-1 p-1 rounded-xl mb-5 w-fit" style={{ background: CARD }} data-testid="portfolio-tabs">
          {[{ key: 'portfolio', label: 'Product Portfolio', icon: Package }, { key: 'rnd', label: 'R&D Lab', icon: FlaskConical }].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold transition-all"
              style={{ background: activeTab === t.key ? `${T}15` : 'transparent', color: activeTab === t.key ? T : '#555' }}
              data-testid={`tab-${t.key}`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════════════════════════════
            PRODUCT PORTFOLIO TAB
           ═══════════════════════════════════════════ */}
        {activeTab === 'portfolio' && (
          <div className="space-y-4">
            {/* Filter bar */}
            <div className="flex items-center gap-3 flex-wrap">
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="px-3 py-2 rounded-lg text-[11px] text-white focus:outline-none" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="sort-select">
                {SORT_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
              <div className="flex gap-1">
                {QUICK_FILTERS.map(f => (
                  <button key={f} onClick={() => setQuickFilter(f)}
                    className="px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all"
                    style={{ background: quickFilter === f ? `${T}15` : 'transparent', color: quickFilter === f ? T : '#555', border: `1px solid ${quickFilter === f ? `${T}40` : BORDER}` }}
                    data-testid={`filter-${f.toLowerCase().replace(/\s/g, '-')}`}>{f}</button>
                ))}
              </div>
              <span className="text-[10px] text-gray-600 ml-auto">{filtered.length} products</span>
            </div>

            {/* Product Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sku-grid">
              {filtered.map(sku => {
                const sc = STATUS_COLORS[sku.status] || STATUS_COLORS['Wave-Ready'];
                return (
                  <div key={sku.id} className="rounded-xl overflow-hidden transition-all hover:shadow-lg" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid={`sku-card-${sku.id}`}>
                    <Link to={`/portfolio/${sku.id}`} className="block group" data-testid={`sku-card-link-${sku.id}`}>
                      <div className="relative h-32 overflow-hidden cursor-pointer">
                        <img src={sku.image} alt={sku.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                        <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>{sku.status}</span>
                        <span className="absolute bottom-2.5 left-2.5 text-[9px] text-white/70 opacity-0 group-hover:opacity-100 transition-opacity font-semibold">View details →</span>
                      </div>
                    </Link>
                    <div className="p-4 space-y-3">
                      <Link to={`/portfolio/${sku.id}`} className="block">
                        <h3 className="text-sm font-bold text-white leading-tight hover:text-[#00F5FF] transition-colors">{sku.name}</h3>
                      </Link>

                      {/* Key Metrics */}
                      <div className="grid grid-cols-5 gap-1.5">
                        {[
                          { l: 'Revenue', v: sku.revenue },
                          { l: 'ROAS', v: sku.roas },
                          { l: 'Velocity', v: sku.velocity === '—' ? '—' : `${sku.velocity}x` },
                          { l: 'Impressions', v: sku.impressions },
                          { l: 'Success', v: `${sku.successRate}%` },
                        ].map(m => (
                          <div key={m.l} className="text-center p-1.5 rounded" style={{ background: BG }}>
                            <p className="text-[6px] text-gray-600 uppercase">{m.l}</p>
                            <p className="text-[10px] font-bold text-white">{m.v}</p>
                          </div>
                        ))}
                      </div>

                      {/* AI Insight */}
                      <p className="text-[9px] text-gray-500 flex items-start gap-1.5 leading-relaxed">
                        <Sparkles className="h-3 w-3 flex-shrink-0 mt-0.5" style={{ color: T }} />{sku.aiInsight}
                      </p>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Link to="/build-wave" className="flex-1">
                          <button className="w-full px-2 py-2 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 hover:brightness-110 transition-all"
                                  style={{ background: 'transparent', color: '#FF4D94', border: '1px solid rgba(255,77,148,0.4)' }}
                                  data-testid={`launch-wave-${sku.id}`}>
                            <Zap className="h-3 w-3" />Plan a wave
                          </button>
                        </Link>
                        <button onClick={() => setCalendarSku(sku)} className="px-3 py-2 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition-all" style={{ background: `${T}10`, color: T, border: `1px solid ${T}30` }} data-testid={`calendar-${sku.id}`}>
                          <Calendar className="h-3 w-3" />Calendar
                        </button>
                        <button className="px-3 py-2 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition-all" style={{ background: BG, color: '#888', border: `1px solid ${BORDER}` }} data-testid={`history-${sku.id}`}>
                          <BarChart3 className="h-3 w-3" />History
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════
            R&D LAB TAB
           ═══════════════════════════════════════════ */}
        {activeTab === 'rnd' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-base font-bold text-white mb-1 flex items-center gap-2">
                <FlaskConical className="h-4 w-4" style={{ color: P }} />Test Unreleased Products with Small Creator Groups
              </h2>
              <p className="text-xs text-gray-500">Send unreleased products to a small group of trusted creators and get real feedback before launching a full Wave.</p>
              {isDemo && profile?.rnd_label && (
                <p className="text-[11px] text-gray-600 italic mt-1">{RND_LABEL}</p>
              )}
            </div>

            {/* ═══ SIMPLE STEP CARD ═══ */}
            <div className="rounded-xl p-6 space-y-5" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="rnd-form">
              {/* Step 1: Product */}
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-2">Step 1 &mdash; Select Unreleased Product</p>
                <select value={testProduct} onChange={e => setTestProduct(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg text-sm text-white focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="rnd-product-select">
                  <option value="">Choose from upcoming catalog...</option>
                  {UNRELEASED.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value="custom">+ Add New Product</option>
                </select>
              </div>

              {/* Step 2: Test Size */}
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-2">Step 2 &mdash; Choose Test Size</p>
                <div className="grid grid-cols-3 gap-3">
                  {[10, 25, 50].map(n => (
                    <button key={n} onClick={() => setTestSize(n)}
                      className="py-3 rounded-lg text-center transition-all"
                      style={{ background: testSize === n ? `${T}15` : BG, color: testSize === n ? T : '#666', border: `1px solid ${testSize === n ? `${T}50` : BORDER}` }}
                      data-testid={`test-size-${n}`}>
                      <p className="text-lg font-black">{n}</p>
                      <p className="text-[9px] text-gray-600">Creators</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Duration */}
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-2">Step 3 &mdash; Choose Test Duration</p>
                <div className="grid grid-cols-3 gap-3">
                  {['1 Week', '2 Weeks', '4 Weeks'].map(d => (
                    <button key={d} onClick={() => setTestDuration(d)}
                      className="py-3 rounded-lg text-center transition-all"
                      style={{ background: testDuration === d ? `${P}15` : BG, color: testDuration === d ? P : '#666', border: `1px solid ${testDuration === d ? `${P}50` : BORDER}` }}
                      data-testid={`test-duration-${d.replace(/\s/g, '-').toLowerCase()}`}>
                      <p className="text-sm font-bold">{d}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 4: Notes */}
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-2">Step 4 &mdash; Special Instructions (optional)</p>
                <input type="text" value={testNotes} onChange={e => setTestNotes(e.target.value)}
                  placeholder="e.g., Focus on taste feedback, video reactions preferred..."
                  className="w-full px-4 py-2.5 rounded-lg text-xs text-white placeholder-gray-600 focus:outline-none" style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid="rnd-notes" />
              </div>

              {/* Launch */}
              <button onClick={handleLaunchTest} disabled={!testProduct}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-black flex items-center justify-center gap-2 disabled:opacity-30 transition-all"
                style={{ background: T, boxShadow: `0 0 20px ${T}30` }} data-testid="launch-test-btn">
                {testLaunched ? <><Check className="h-4 w-4" />Test Launched!</> : <><Send className="h-4 w-4" />Send Product & Launch Test</>}
              </button>
            </div>

            {/* ═══ ACTIVE & COMPLETED TESTS ═══ */}
            <div>
              <h3 className="text-sm font-bold text-white mb-3">Active & Completed Tests</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="test-results">
                {MOCK_TESTS.map(test => (
                  <div key={test.id} className="rounded-xl overflow-hidden" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid={`test-${test.id}`}>
                    <div className="flex gap-4 p-4">
                      <img src={test.image} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-xs font-bold text-white truncate">{test.product}</h4>
                          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
                            style={{ background: test.status === 'Completed' ? '#4ade8015' : `${T}15`, color: test.status === 'Completed' ? '#4ade80' : T }}>{test.status}</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mb-3">{test.creators} creators &bull; {test.duration}</p>

                        {/* Feedback Metrics */}
                        <div className="flex gap-2 flex-wrap">
                          <span className="px-2 py-1 rounded text-[9px] font-semibold" style={{ background: BG, color: T, border: `1px solid ${BORDER}` }}>Eng: {test.engagement}</span>
                          <span className="px-2 py-1 rounded text-[9px] font-semibold" style={{ background: BG, color: '#4ade80', border: `1px solid ${BORDER}` }}>Sentiment: {test.sentiment}%</span>
                          <span className="px-2 py-1 rounded text-[9px] font-semibold" style={{ background: BG, color: P, border: `1px solid ${BORDER}` }}>Purchase Intent: {test.purchaseIntent}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 pb-4 space-y-2">
                      <div className="p-2.5 rounded-lg" style={{ background: BG }}>
                        <p className="text-[8px] text-gray-600 uppercase font-semibold mb-0.5">Top Positive Feedback</p>
                        <p className="text-[10px] text-gray-400">{test.positiveFeedback}</p>
                      </div>
                      <div className="p-2.5 rounded-lg" style={{ background: BG }}>
                        <p className="text-[8px] text-gray-600 uppercase font-semibold mb-0.5">Top Suggested Improvement</p>
                        <p className="text-[10px] text-gray-400">{test.improvements}</p>
                      </div>
                      <Link to="/build-wave">
                        <button className="w-full py-2.5 rounded-lg text-[10px] font-bold text-white flex items-center justify-center gap-1.5 mt-2 hover:brightness-110 transition-all"
                                style={{ background: '#FF4D94', boxShadow: '0 0 14px rgba(255,77,148,0.32)' }}
                                data-testid={`launch-full-wave-${test.id}`}>
                          <ArrowRight className="h-3 w-3" />Plan a full wave from this test
                        </button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════
            WAVE CALENDAR MODAL
           ═══════════════════════════════════════════ */}
        <Dialog open={!!calendarSku} onOpenChange={(open) => { if (!open) setCalendarSku(null); }}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto text-white p-0" style={{ background: CARD, borderColor: BORDER }}>
            {calendarSku && (
              <>
                <div className="p-6 pb-0">
                  <DialogHeader>
                    <DialogTitle className="text-white text-lg font-black">{calendarSku.name} — Wave Calendar 2026</DialogTitle>
                    <p className="text-xs text-gray-500 sr-only">Yearly wave calendar showing all planned and completed waves</p>
                  </DialogHeader>
                </div>
                <div className="p-6 space-y-4">
                  {/* Year Timeline */}
                  <div className="flex gap-1" data-testid="calendar-timeline">
                    {MONTHS.map(m => {
                      const hasWave = calendarSku.calendar.some(w => w.month === m);
                      const tentpole = TENTPOLES[m];
                      return (
                        <div key={m} className="flex-1 text-center">
                          <div className="h-8 rounded flex items-center justify-center text-[9px] font-bold transition-all"
                            style={{ background: hasWave ? `${T}20` : BG, color: hasWave ? T : '#444', border: `1px solid ${hasWave ? `${T}30` : BORDER}` }}>{m}</div>
                          {tentpole && <p className="text-[7px] mt-1 leading-tight" style={{ color: P }}>{tentpole}</p>}
                        </div>
                      );
                    })}
                  </div>

                  {/* Wave Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="calendar-waves">
                    {calendarSku.calendar.map(w => {
                      // Pick colour palette per status — extracted out of JSX to
                      // avoid nested ternaries inline.
                      let chipBg = `${P}15`, chipFg = P;
                      if (w.status === 'active') { chipBg = `${T}15`; chipFg = T; }
                      else if (w.status === 'completed') { chipBg = '#4ade8015'; chipFg = '#4ade80'; }
                      return (
                      <div key={w.wave} className="p-4 rounded-xl" style={{ background: BG, border: `1px solid ${BORDER}` }}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-bold text-white">{w.wave}</h4>
                          <span className="text-[8px] px-2 py-0.5 rounded-full font-bold capitalize"
                            style={{ background: chipBg, color: chipFg }}>{w.status}</span>
                        </div>
                        <div className="flex gap-3 text-[10px] text-gray-500 mb-3">
                          <span>{w.month} 2026</span>
                          <span>Budget: <span className="text-white font-semibold">{w.budget}</span></span>
                          {w.roas && <span>ROAS: <span className="font-semibold" style={{ color: T }}>{w.roas}</span></span>}
                          {w.creators && <span>{w.creators} creators</span>}
                        </div>
                        <div className="flex gap-2">
                          <button className="px-2.5 py-1.5 rounded text-[9px] font-semibold" style={{ background: CARD, color: '#888', border: `1px solid ${BORDER}` }}>Edit Wave</button>
                          <button className="px-2.5 py-1.5 rounded text-[9px] font-semibold" style={{ background: CARD, color: '#888', border: `1px solid ${BORDER}` }}>Duplicate</button>
                          {w.status === 'completed' && <button className="px-2.5 py-1.5 rounded text-[9px] font-semibold" style={{ background: `${T}10`, color: T, border: `1px solid ${T}30` }}>View Pulse</button>}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Recommended Tentpole & Seasonal Windows</h4>
                    <div className="flex gap-2 flex-wrap">
                      {filledTentpoles.map(([month, name]) => {
                        const already = calendarSku.calendar.some(w => w.month === month);
                        return (
                          <div key={month} className="p-3 rounded-lg flex items-center gap-3" style={{ background: BG, border: `1px solid ${BORDER}` }}>
                            <div>
                              <p className="text-[10px] font-bold text-white">{name}</p>
                              <p className="text-[8px] text-gray-600">{month} 2026</p>
                            </div>
                            {already ? (
                              <span className="text-[8px] font-bold" style={{ color: '#4ade80' }}>Scheduled</span>
                            ) : (
                              <Link to="/build-wave">
                                <button className="px-2.5 py-1.5 rounded text-[8px] font-bold" style={{ background: `${P}15`, color: P, border: `1px solid ${P}30` }}>Schedule Wave</button>
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
